/**
 * MOCK · CANONICAL (CT-5273) — the values a widget shows for ONE entity.
 *
 * Derived from the instance uuid AND the entity id, so two records of the same
 * type render the same layout with different numbers. That is the claim the
 * Overview makes — "the dashboard published for this type, fed with THIS
 * record's data" — and a seed returning one constant would let a broken scope
 * wiring pass unnoticed.
 */

/**
 * Stable pseudo-random from a string, so a record's numbers never jump
 * between loads — but ONE character of difference has to move the result by
 * a lot, not by one. A plain rolling sum (`sum*31+charCode`) is linear: `e-1`
 * and `e-2` differ by exactly 1 in the last char code, and %100000 carries
 * that difference through almost unchanged — every KPI on employee 2 read
 * one point off employee 1's, which reads as static data, not per-record
 * data, the moment two consecutive records sit side by side. FNV-1a's XOR +
 * multiply per character (32-bit prime) gives the avalanche a rolling sum
 * doesn't: a 1-char input change flips unrelated bits throughout the output.
 */
const hash = (value) => {
  let h = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    h ^= value.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};

export const buildMockWidgetData = (instanceUuid, entityId, title = 'Widget') => {
  const seed = hash(`${instanceUuid}:${entityId ?? ''}`);
  // The shape a STANDARD widget renders: `{ title, items: [{ id, content }] }`.
  // The earlier `{ value, trend, series }` was a perfectly reasonable analytics
  // payload that the package's renderer knows nothing about, so every widget
  // drew its generic "Widget · No data to display" placeholder while the
  // adapter tests stayed green. Seeds have to speak the renderer's language.
  //
  // `progress`/`label`/`feedbackText` speak `KpiWidget`'s language instead —
  // `mapKpiData` reads those, not `items` — for the two `CUSTOM_FIXED`/`kpi`
  // widgets on the customer Main dashboard (Revenue, Open tickets). Left on
  // every seed rather than branched by widget: a STANDARD renderer ignores
  // fields it doesn't read, the same way a kpi one ignores `items`.
  const value = seed % 1000;
  const target = 1000;
  return {
    title,
    entity_id: entityId ?? null,
    value,
    label: `/ ${target.toLocaleString()}`,
    progress: Math.round((value / target) * 100),
    trend: seed % 2 === 0 ? 'up' : 'down',
    feedbackText: `${seed % 2 === 0 ? '+' : '-'}${(seed % 20) + 1}% vs last quarter`,
    items: Array.from({ length: 3 }, (_, idx) => ({
      id: `${instanceUuid}-m${idx + 1}`,
      content: `M${idx + 1} · ${(seed + idx * 37) % 500}`,
    })),
  };
};
